import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { notificationInboxQueryKeys } from '../api/notification-inbox-query-keys'
import { createNotificationSignalRClient } from '../api/notification-signalr-client'
import type { InAppNotification } from '../api/notifications-types'
import { useMarkNotificationReadMutation } from '../api/use-in-app-notifications'
import { presentLiveNotification } from '../lib/present-live-notification'
import { resolveNotificationHref } from '../lib/resolve-notification-href'

type NotificationSessionProps = {
  tenantId: string
  getAccessToken: () => string | null
}

/** Live inbox for the signed-in shell. Unmount (logout, expiry, user switch) disconnects this session only. */
export function NotificationSession({ tenantId, getAccessToken }: NotificationSessionProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const markRead = useMarkNotificationReadMutation(tenantId)
  const tenantIdRef = useRef(tenantId)
  const tokenRef = useRef(getAccessToken)
  const openRef = useRef<(notification: InAppNotification) => void>(() => undefined)

  tenantIdRef.current = tenantId
  tokenRef.current = getAccessToken

  openRef.current = (notification) => {
    if (!notification.isRead) {
      void markRead.mutateAsync(notification.id).catch(() => undefined)
    }
    const href = resolveNotificationHref(notification.actionUrl)
    if (href.kind === 'external') {
      window.location.assign(href.url)
      return
    }
    if (href.kind === 'internal') navigate(href.path)
  }

  useEffect(() => {
    const hub = createNotificationSignalRClient({
      getAccessToken: () => tokenRef.current(),
    })
    const unsubscribe = hub.subscribe((notification) => {
      const activeTenantId = tenantIdRef.current
      void queryClient.invalidateQueries({
        queryKey: notificationInboxQueryKeys.unread(activeTenantId),
      })
      void queryClient.invalidateQueries({
        queryKey: notificationInboxQueryKeys.list(activeTenantId),
      })
      presentLiveNotification(notification, (item) => openRef.current(item))
    })
    void hub.connectIfNeeded()
    return () => {
      unsubscribe()
      void hub.disconnect()
    }
  }, [queryClient])

  return null
}
